import { CircularLoader } from '@modules/common/components/CircularLoader';
import { BANNER_HEIGHT } from '@modules/infoBanner/constants';
import { useIsInfoBannerVisible } from '@modules/infoBanner/hooks';
import { OracleEmptySearchView } from '@modules/oracle/components/OracleEmptySearchView';
import { WisdomChatConversations } from '@modules/oracle/components/WisdomChatConversations';
import { WisdomChatSidebar } from '@modules/oracle/components/WisdomChatSidebar';
import { SHOULD_OPEN_WISDOM_NAVBAR } from '@modules/oracle/constants';
import { useCreateOracleSearchSession } from '@modules/oracle/hooks';
import { useFetchChatByToken } from '@modules/oracle/hooks/useFetchChatByToken';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { getWisdomRoute, trackWisdomEvents } from '@modules/oracle/utils';
import { Stack } from '@mui/material';
import Locker from 'lockr';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useParseSavedSlugInfo from 'v2/core/services/savedSlug/useParseSavedSlugInfo';
import { usePostPromptIfSearchParamExists } from './usePostPromptIfSearchParamExists';

export function ChatContainer() {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(
    Locker.get(SHOULD_OPEN_WISDOM_NAVBAR) ?? true,
  );
  const { token: viewTokenFromURL } = useParseSavedSlugInfo();
  const isInfoBannerVisible = useIsInfoBannerVisible();
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const shouldShowEmptyView = useOracleSearchStore(
    ({ data }) => !data.currentPromptID,
  );
  const { createWebSocketConnection, isLoading: isWebSocketConnectionLoading } =
    useCreateOracleSearchSession();

  const navigate = useNavigate();
  const isPromptSearchParamResponseLoading = usePostPromptIfSearchParamExists();

  const {
    promptByID,
    currentPromptID,
    chat: chatFromFetchAPI,
    isLoading: isChatDataLoading,
  } = useFetchChatByToken({
    token: viewTokenFromURL,
    skip: !viewTokenFromURL || !!location.state?.skipReset,
    onCompleted: (data) => {
      if (data.getChatByToken) {
        // Redirect to public route, if chat was fetched by direct editToken or viewToken in URL
        // Example: /wisdom/:editToken OR /wisdom/:viewToken -> /wisdom/chat-title-view-token-UUID
        navigate(
          getWisdomRoute({
            name: data.getChatByToken.chat.title,
            token: data.getChatByToken.chat.viewToken,
          }),
          {
            replace: true,
          },
        );
      }
    },
  });

  const dependencies = {
    viewTokenFromURL,
    skipReset: !!location.state?.skipReset,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  // Create/Reset websocket connection on viewTokenFromURL(a.k.a URL) change
  useEffect(() => {
    if (!dependenciesRef.current.skipReset) {
      dispatch({
        type: OracleSearchActionType.RESET,
      });

      const disconnect = createWebSocketConnection({
        withSessionID:
          viewTokenFromURL === chatFromFetchAPI?.viewToken ||
          viewTokenFromURL === chatFromFetchAPI?.editToken
            ? chatFromFetchAPI?.sessionID
            : undefined,
      });

      return () => {
        if (!dependenciesRef.current.skipReset) {
          disconnect?.();
        }
      };
    }
  }, [
    viewTokenFromURL,
    chatFromFetchAPI?.viewToken,
    chatFromFetchAPI?.sessionID,
    createWebSocketConnection,
    dispatch,
    chatFromFetchAPI?.editToken,
  ]);

  // Reset store on unmount
  useEffect(() => {
    return () => {
      dispatch({
        type: OracleSearchActionType.RESET,
      });
    };
  }, [dispatch]);

  // Persist chat data to store on presence of chat data
  useEffect(() => {
    // Ignore the chat data if the viewTokenFromURL has changed
    if (
      dependenciesRef.current.viewTokenFromURL !==
        chatFromFetchAPI?.viewToken &&
      dependenciesRef.current.viewTokenFromURL !== chatFromFetchAPI?.editToken
    ) {
      return;
    }

    if (!dependenciesRef.current.skipReset && !isChatDataLoading) {
      dispatch({
        type: OracleSearchActionType.SET_DATA,
        payload: {
          chat: chatFromFetchAPI,
          promptByID,
          currentPromptID,
        },
      });
    }
  }, [
    isChatDataLoading,
    chatFromFetchAPI,
    dispatch,
    promptByID,
    currentPromptID,
  ]);

  const renderChatDetailView = () => {
    if (
      isWebSocketConnectionLoading ||
      isChatDataLoading ||
      isPromptSearchParamResponseLoading
    ) {
      return <CircularLoader stackProps={{ width: '100%' }} />;
    }

    if (shouldShowEmptyView) {
      return <OracleEmptySearchView />;
    }

    return <WisdomChatConversations />;
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((isSidebarOpen) => {
      Locker.set(SHOULD_OPEN_WISDOM_NAVBAR, !isSidebarOpen);
      try {
        trackWisdomEvents({
          eventName: isSidebarOpen
            ? 'Wisdom - Sidebar Closed'
            : 'Wisdom - Sidebar Opened',
        });
      } catch {}
      return !isSidebarOpen;
    });
  };

  const renderChatContainer = () => {
    return (
      <Stack direction="row" display="flex" sx={{ height: '100%' }}>
        <WisdomChatSidebar
          isOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
        />
        {renderChatDetailView()}
      </Stack>
    );
  };

  return (
    <Stack
      sx={{
        height: isInfoBannerVisible
          ? `calc(100vh - ${BANNER_HEIGHT}px)`
          : '100vh',
      }}
    >
      {renderChatContainer()}
    </Stack>
  );
}
